import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { TaskForAsyncTaskFunction } from 'ember-concurrency';
import type EmployeeModel from 'garaje/models/employee';
import type FloorModel from 'garaje/models/floor';
import type { ResourceAmenityType } from 'garaje/types/generic-resources/ResourceTypeConfig';

import { ADD_SPOTS_OPTION } from '../add-parking-options/component';
import type { StepConfig } from '../ui/classic/multi-step-modal/component';

enum STEP {
  LANDING = 'landing',
  ADD_OPTIONS = 'add-options',
  FORM = 'form',
}

interface CreateParkingModalArgs {
  resourceTypeId: string;
  resourceTypeLabel: string;
  amenities: ResourceAmenityType[];
  amenitiesMap: { [key: string]: string };
  initialStep?: STEP;
  employees: EmployeeModel[];
  floors: FloorModel[];
  hasMoreEmployeePages: boolean;
  loadMoreEmployeesTask: TaskForAsyncTaskFunction<unknown, () => Promise<void>>;
  onEmployeeSearchTask: TaskForAsyncTaskFunction<unknown, (term: string) => Promise<void>>;
  onClose: () => void;
}

export default class CreateParkingModalComponent extends Component<CreateParkingModalArgs> {
  @tracked addSpotsOption: ADD_SPOTS_OPTION | null = null;

  @tracked modalContext: {
    stepContext: { currentStep: string; navigateToStep: (stepId: string) => Promise<void> };
  } | null = null;

  get steps(): StepConfig[] {
    return [
      {
        id: STEP.LANDING,
        label: 'Get started with parking',
        component: 'parking-landing',
      },
      {
        id: STEP.ADD_OPTIONS,
        label: 'Add parking spots',
        component: 'add-parking-options',
        canNavigate: () => this.canNavigateToAddOptions(),
      },
      {
        id: STEP.FORM,
        label: () => this.getFormStepLabel(),
        component: 'add-parking-form',
        canNavigate: () => this.canNavigateToForm(),
      },
    ];
  }

  get initialStep(): string {
    return this.args.initialStep || STEP.LANDING;
  }

  @action
  canNavigateToAddOptions(): boolean {
    // Can always navigate to add options from landing
    return true;
  }

  @action
  canNavigateToForm(): boolean {
    // Can navigate to form if an option is selected
    return this.addSpotsOption !== null;
  }

  @action
  getFormStepLabel(): string {
    return this.addSpotsOption === ADD_SPOTS_OPTION.BULK ? 'Bulk add parking spots' : 'Add a parking spot';
  }

  @action
  setAddParkingOption(option: ADD_SPOTS_OPTION): void {
    this.addSpotsOption = option;
  }

  @action
  setModalContext(context: {
    stepContext: { currentStep: string; navigateToStep: (stepId: string) => Promise<void> };
  }): void {
    this.modalContext = context;
  }

  @action
  async handleContinue(): Promise<void> {
    if (!this.modalContext) return;

    // Get the current step index and navigate to the next step
    const currentIndex = this.steps.findIndex((step) => step.id === this.modalContext?.stepContext.currentStep);
    if (currentIndex >= 0 && currentIndex < this.steps.length - 1) {
      const nextStep = this.steps[currentIndex + 1];
      if (nextStep) {
        await this.modalContext.stepContext.navigateToStep(nextStep.id);
      }
    }
  }

  @action
  handleCloseModal(): void {
    this.args.onClose();
  }
}
